/**
 * StatusGlyph + statusTone — the single shared source of status semantics
 * (ADR-0083). Status colour is ONE design token of SIX semantic tones,
 * each rendered with a per-tone glyph so colour is never the only signal
 * (WCAG 1.4.1; design intent §D + §G.7).
 *
 * `statusTone(status)` is the canonical mapping a status string → tone;
 * unknown statuses fall back to `inert` (the auto-grow guarantee — a
 * status vocabulary may grow at any time and must never crash a render or
 * invent a colour). It replaces the three drifted copies (list pill, the
 * sheet's StatusChipForInvoice, the print route's local statusTone).
 *
 * The six tones map to dedicated AA-verified `--color-status-<tone>-*`
 * token pairs (raw Tailwind tint pairs are banned). The two greens are
 * told apart by both colour (positive=leaf-green, brand-active=teal) AND
 * glyph (positive=check, brand-active=dot).
 */

import { Check, Clock, AlertTriangle, ArrowRight, Circle, Minus } from "lucide-react";
import type { ReactElement } from "react";

export type SemanticTone =
  | "positive"
  | "attention"
  | "negative"
  | "in-motion"
  | "brand-active"
  | "inert";

interface ToneSpec {
  /** Background + text token pair (both AA-verified ≥4.5:1). */
  bg: string;
  text: string;
  /** The distinguishing glyph for this tone. */
  glyph: "check" | "clock" | "alert" | "arrow" | "dot" | "dash";
}

const TONE_SPECS: Readonly<Record<SemanticTone, ToneSpec>> = {
  positive: {
    bg: "--color-status-positive-bg",
    text: "--color-status-positive-text",
    glyph: "check",
  },
  attention: {
    bg: "--color-status-attention-bg",
    text: "--color-status-attention-text",
    glyph: "clock",
  },
  negative: {
    bg: "--color-status-negative-bg",
    text: "--color-status-negative-text",
    glyph: "alert",
  },
  "in-motion": {
    bg: "--color-status-in-motion-bg",
    text: "--color-status-in-motion-text",
    glyph: "arrow",
  },
  "brand-active": {
    bg: "--color-status-brand-active-bg",
    text: "--color-status-brand-active-text",
    glyph: "dot",
  },
  inert: {
    bg: "--color-status-inert-bg",
    text: "--color-status-inert-text",
    glyph: "dash",
  },
};

export function toneSpec(tone: string): ToneSpec {
  return TONE_SPECS[(tone as SemanticTone)] ?? TONE_SPECS.inert;
}

/**
 * The canonical status → tone map (ADR-0083). One source consumed by list
 * badges, the row colour-edge, the detail header badge, and the print
 * route. A module's status vocabulary may grow; anything unmapped resolves
 * to `inert` (never crashes, never invents a colour). Status strings are
 * matched case-insensitively.
 *
 * Tone vocabulary (ADR-0083 §Decision.1):
 *   positive     — paid, accepted, fully_applied, closed
 *   attention    — pending, partial, viewed
 *   negative     — overdue, rejected, declined, void(ed)
 *   in-motion    — open
 *   brand-active — sent, active, approved, converted
 *   inert        — draft, archived, expired, cancelled, + everything unknown
 */
const STATUS_TONE_MAP: Readonly<Record<string, SemanticTone>> = {
  paid: "positive",
  accepted: "positive",
  fully_applied: "positive",
  closed: "positive",

  pending: "attention",
  partial: "attention",
  partially_paid: "attention",
  viewed: "attention",

  overdue: "negative",
  rejected: "negative",
  declined: "negative",
  void: "negative",
  voided: "negative",

  open: "in-motion",

  // error-collection issue lifecycle (ADR-0121): confirmed = being worked
  // (attention/clock), fixed = resolved (positive/check). `open` and
  // `closed` already map above (in-motion / positive) and are reused as-is.
  confirmed: "attention",
  fixed: "positive",

  // Stock-document posting (SMV-E, UI review BLOCKING 2). `posted` used to
  // fall through to `inert` — the same grey as draft, archived and cancelled —
  // so a warehouse lead scanning 87 transfer rows could not tell a live one
  // from a deleted one, and every colour test passed because the fallback is
  // deliberate. Three statuses, three tones: the stock is on the ledger, some
  // of it is, none of it is. (`partial` already maps to attention above and is
  // reused as-is — one word, one tone, everywhere.)
  posted: "positive",
  not_posted: "negative",

  // Goods arrival (GA-E, UI review §2 and accessibility finding 4). Six
  // states, registered BEFORE the screens ship: an unregistered one falls to
  // `inert`, the grey `archived` and `cancelled` share, and the four arrivals
  // that need a person stop jumping out of the fifty that do not (the E.11.a
  // lesson). Short is red and Over is amber — two different conversations with
  // a supplier. `resolved` is grey on purpose: nothing is owed on it.
  uncounted: "in-motion",
  counting: "attention",
  counted: "positive",
  short: "negative",
  over: "attention",
  resolved: "inert",
  // Goods arrival slice F: a QuickBooks item receipt on the queue. Registered
  // on purpose, not left to the fallback: grey because it is never work —
  // QuickBooks already received it and Ken keeps no count on it (ADR-0218/0220).
  received_in_quickbooks: "inert",

  // Texting statuses (W8-03-S14-T4, UI review 2026-10-22 MUST-FIX 1). Unregistered,
  // every one fell to `inert`, so on the Messages table Refused and Failed were
  // the same grey as Delivered and only Sent had a colour. Delivered is done,
  // Undelivered needs a look, Refused and Failed are red, Waiting is in motion,
  // Queued and Sending share Sent's brand colour. (`sent` maps below.)
  delivered: "positive",
  undelivered: "attention",
  refused: "negative",
  failed: "negative",
  waiting: "in-motion",
  queued: "brand-active",
  sending: "brand-active",

  sent: "brand-active",
  active: "brand-active",
  approved: "brand-active",
  converted: "brand-active",

  draft: "inert",
  archived: "inert",
  expired: "inert",
  cancelled: "inert",
};

export function statusTone(status: string | null | undefined): SemanticTone {
  if (!status) return "inert";
  return STATUS_TONE_MAP[status.toLowerCase()] ?? "inert";
}

/** Render the glyph for a tone. Always `aria-hidden` — the visible label
 *  text carries the meaning for assistive tech; the glyph is the
 *  non-colour visual cue for sighted users. */
export function StatusGlyph({
  tone,
  size = 12,
}: {
  tone: SemanticTone | string;
  size?: number;
}): ReactElement {
  const spec = toneSpec(tone);
  const common = { size, "aria-hidden": true as const, className: "shrink-0" };
  switch (spec.glyph) {
    case "check":
      return <Check {...common} />;
    case "clock":
      return <Clock {...common} />;
    case "alert":
      return <AlertTriangle {...common} />;
    case "arrow":
      return <ArrowRight {...common} />;
    case "dash":
      return <Minus {...common} />;
    case "dot":
    default:
      return <Circle {...common} fill="currentColor" strokeWidth={0} />;
  }
}
